// Month-End Close Automation Agent UI (Polished)
// Additions: close progress gauge, task completion chart, status timeline,
// bulk approve actions, period selector.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, DonutChart, RadialGauge, StatusPill,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    mecDag, mecAccruals, mecIntercompany, mecDepreciation, mecRevRec, mecStatus,
    autonomousWorkflowSummary,
} from './api';

const TABS = ['Close Checklist (DAG)', 'Accrual Generation', 'Intercompany Recon', 'Depreciation', 'Revenue Recognition', 'Status Report'];

export default function MonthEndCloseAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);

    const [dag, setDag] = useState(null);
    const [accruals, setAccruals] = useState(null);
    const [interco, setInterco] = useState(null);
    const [depreciation, setDepreciation] = useState(null);
    const [revRec, setRevRec] = useState(null);
    const [status, setStatus] = useState(null);
    const [periodId, setPeriodId] = useState('');

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        autonomousWorkflowSummary().then(r => r?.data?.success && setSummary(r.data.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !dag) { const r = await run(() => mecDag()); if (r?.data?.success) setDag(r.data.data); }
            else if (tab === 2 && !interco) { const r = await run(() => mecIntercompany()); if (r?.data?.success) setInterco(r.data.data); }
            else if (tab === 5 && !status) { const r = await run(() => mecStatus(null, true)); if (r?.data?.success) setStatus(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Overall Progress', value: dag?.overall_progress != null ? `${dag.overall_progress}%` : '—', accent: dag?.overall_progress === 100 ? 'success' : 'warning' },
        { label: 'Completed Steps', value: (dag?.steps || []).filter(s => s.status === 'completed').length, accent: 'success' },
        { label: 'In Progress', value: (dag?.steps || []).filter(s => s.status === 'in_progress').length, accent: 'warning' },
        { label: 'Blockers', value: status?.blockers ?? '—', accent: status?.blockers > 0 ? 'danger' : 'success' },
    ];

    // Status donut data
    const statusDonutData = (() => {
        if (!dag?.steps) return [];
        const counts = dag.steps.reduce((acc, s) => {
            acc[s.status] = (acc[s.status] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    })();

    const renderTab = () => {
        switch (tab) {
            case 0:
                if (!dag) return <LoadingBlock rows={5} />;
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Close Checklist (DAG Execution)"
                            subtitle="30+ tasks in dependency order. Parallel where possible, blocking on prerequisites."
                            actions={<IconButton size="small" onClick={() => { setDag(null); run(() => mecDag()).then(r => r?.data?.success && setDag(r.data.data)); }}><RefreshIcon fontSize="small" /></IconButton>}
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>Close Progress</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={dag.overall_progress ?? 0}
                                            label={dag.overall_progress === 100 ? 'Complete' : 'In Progress'}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Task Status Distribution</Typography>
                                    {statusDonutData.length > 0 ? (
                                        <DonutChart data={statusDonutData} height={240} />
                                    ) : <EmptyState message="No tasks" />}
                                </Paper>
                            </Grid>
                        </Grid>
                        <DrillTable
                            columns={[
                                { key: 'name', label: 'Step' },
                                { key: 'dependencies', label: 'Dependencies', render: r => (r.dependencies || []).join(', ') || '—' },
                                { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                { key: 'completion_pct', label: '%', align: 'right', render: r => r.completion_pct != null ? `${r.completion_pct}%` : '—' },
                            ]}
                            rows={dag.steps || []}
                            maxHeight={420}
                        />
                        <AIAnalysisBox text={dag.ai_analysis} />
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Accrual Generation" subtitle="Auto-identify and create required accrual entries" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Period ID" size="small" value={periodId} onChange={e => setPeriodId(e.target.value)} sx={{ width: 180 }} />
                            <Button variant="contained" size="small" disabled={!periodId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => mecAccruals(periodId, true));
                                    if (r?.data?.success) setAccruals(r.data.data);
                                }}>Generate</Button>
                        </Stack>
                        {accruals && (
                            <>
                                <DrillTable
                                    columns={[
                                        { key: 'source', label: 'Source' },
                                        { key: 'type', label: 'Type' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'account', label: 'Account' },
                                        { key: 'confidence', label: 'Confidence', render: r => (
                                            <Chip size="small"
                                                color={r.confidence === 'high' ? 'success' : r.confidence === 'medium' ? 'warning' : 'default'}
                                                label={r.confidence} />
                                        )},
                                        { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Post</Button> },
                                    ]}
                                    rows={accruals.candidates || []}
                                    maxHeight={420}
                                />
                                <AIAnalysisBox text={accruals.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            case 2:
                return interco ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Intercompany Reconciliation" subtitle="Match IC-receivable/payable across entities" />
                        <DrillTable
                            columns={[
                                { key: 'entity', label: 'Entity' },
                                { key: 'account', label: 'Account' },
                                { key: 'balance', label: 'Balance', align: 'right', render: r => fmtMoney(r.balance) },
                                { key: 'match_status', label: 'Match Status', render: r => <StatusPill status={r.match_status} /> },
                            ]}
                            rows={interco.entries || []}
                        />
                        <AIAnalysisBox text={interco.ai_analysis} />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 3:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Fixed Asset Depreciation" subtitle="MACRS/SL/DDB depreciation calculation" />
                        <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} onClick={async () => {
                            const r = await run(() => mecDepreciation());
                            if (r?.data?.success) setDepreciation(r.data.data);
                        }} sx={{ alignSelf: 'flex-start' }}>Calculate</Button>
                        {depreciation && (
                            <DrillTable
                                columns={[
                                    { key: 'asset_name', label: 'Asset' },
                                    { key: 'method', label: 'Method' },
                                    { key: 'monthly_amount', label: 'Monthly', align: 'right', render: r => fmtMoney(r.monthly_amount) },
                                    { key: 'accumulated', label: 'Accumulated', align: 'right', render: r => fmtMoney(r.accumulated) },
                                    { key: 'net_book_value', label: 'NBV', align: 'right', render: r => <b>{fmtMoney(r.net_book_value)}</b> },
                                ]}
                                rows={depreciation.assets || []}
                                maxHeight={420}
                            />
                        )}
                    </Stack>
                );
            case 4:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Revenue Recognition (ASC 606)" subtitle="Performance obligation-based recognition" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Period ID" size="small" value={periodId} onChange={e => setPeriodId(e.target.value)} sx={{ width: 180 }} />
                            <Button variant="contained" size="small" disabled={!periodId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => mecRevRec(periodId));
                                    if (r?.data?.success) setRevRec(r.data.data);
                                }}>Load</Button>
                        </Stack>
                        {revRec && (
                            <DrillTable
                                columns={[
                                    { key: 'contract', label: 'Contract', render: r => r.contract || r.schedule },
                                    { key: 'revenue_amount', label: 'Revenue', align: 'right', render: r => fmtMoney(r.revenue_amount) },
                                    { key: 'recognized', label: 'Recognized', align: 'right', render: r => fmtMoney(r.recognized) },
                                    { key: 'deferred', label: 'Deferred', align: 'right', render: r => fmtMoney(r.deferred) },
                                    { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                ]}
                                rows={revRec.schedules || []}
                                maxHeight={420}
                            />
                        )}
                    </Stack>
                );
            case 5:
                return status ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Close Status Report" subtitle="Real-time progress with bottleneck identification" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>Completion</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge value={status.completion_pct ?? 0} label={`${status.completion_pct || 0}%`} />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <SummaryGrid cards={[
                                    { label: 'Tasks Done', value: status.tasks_done ?? '—', accent: 'success' },
                                    { label: 'Blockers', value: status.blockers ?? 0, accent: status.blockers > 0 ? 'danger' : 'success' },
                                ]} cols={2} />
                            </Grid>
                        </Grid>
                        <AIAnalysisBox text={status.ai_summary} title="AI Status Summary" />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Month-End Close Agent"
            subtitle="Agentic Autonomous Workflow — close DAG, accruals, intercompany, depreciation, revenue recognition, status."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
